import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

type UseQueryParamStateOptions<T> = {
  key: string;
  defaultValue: T;
  parse: (value: string) => T | null;
  serialize: (value: T) => string;
  replace?: boolean;
};

/**
 * Keeps a piece of state in sync with the URL query string.
 * Parses values via the provided transformer and falls back to the default when parsing fails.
 */
export function useQueryParamState<T>({
  key,
  defaultValue,
  parse,
  serialize,
  replace = false,
}: UseQueryParamStateOptions<T>): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedValue = useMemo<T>(() => {
    const raw = searchParams.get(key);
    const parsed = raw != null ? parse(raw) : null;
    return parsed ?? defaultValue;
  }, [defaultValue, key, parse, searchParams]);

  const [value, setValue] = useState<T>(parsedValue);

  useEffect(() => {
    setValue(parsedValue);
  }, [parsedValue]);

  const updateValue = (next: T) => {
    setValue(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(key, serialize(next));
    setSearchParams(nextParams, { replace });
  };

  return [value, updateValue];
}
